import React, { useState, useCallback, useEffect } from 'react';
import { useGxFeed } from '../hooks/useGxFeed';
import { useGxPolling } from '../hooks/useGxPolling';
import { useGxZones } from '../hooks/useGxZones';
import { useGxSubscription } from '../hooks/useGxSubscription';
import { GxFeedItem, acknowledgeFeedItem, getGxConfig, getEvents } from '../api/guestExperience';
import { LiveFeedView } from '../components/gx/LiveFeedView';
import { ZoneFilterBar } from '../components/gx/ZoneFilterBar';
import { PollingStatusBar } from '../components/gx/PollingStatusBar';
import { LiveNoteInput } from '../components/gx/LiveNoteInput';

interface EventItem {
  id: string;
  name: string;
}

const LEGACY_BALL_ID = 'e1000000-0000-0000-0000-000000000003';

const pageStyles: Record<string, React.CSSProperties> = {
  container: {
    padding: '1rem',
    maxWidth: '600px',
    margin: '0 auto',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    boxSizing: 'border-box' as const,
    width: '100%',
    overflowX: 'hidden' as const,
  },
  header: {
    marginBottom: '1rem',
  },
  title: {
    color: '#1a6b3c',
    fontSize: '1.5rem',
    margin: '0 0 0.25rem 0',
  },
  subtitle: {
    color: '#666',
    fontSize: '0.875rem',
    margin: 0,
  },
  eventSelector: {
    marginBottom: '1rem',
  },
  eventLabel: {
    display: 'block',
    fontSize: '0.75rem',
    fontWeight: 600,
    color: '#999',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
    marginBottom: '0.35rem',
  },
  eventSelect: {
    width: '100%',
    padding: '0.6rem 0.75rem',
    border: '1px solid #ccc',
    borderRadius: '6px',
    fontSize: '0.9rem',
    boxSizing: 'border-box' as const,
    backgroundColor: '#fff',
  },
  mutedBanner: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    padding: '0.75rem 1rem',
    borderRadius: '8px',
    marginBottom: '1rem',
    backgroundColor: '#fff3e0',
    color: '#e65100',
    fontSize: '0.875rem',
    fontWeight: 600,
  },
  section: {
    marginBottom: '1rem',
  },
  sectionLabel: {
    fontSize: '0.75rem',
    fontWeight: 600,
    color: '#999',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
    marginBottom: '0.5rem',
  },
  loading: {
    textAlign: 'center' as const,
    padding: '2rem',
    color: '#666',
  },
  noteInputWrapper: {
    position: 'sticky' as const,
    bottom: 0,
    backgroundColor: '#fff',
    padding: '0.75rem 0',
    borderTop: '1px solid #e0e0e0',
    zIndex: 10,
  },
};

export function KongosaBoxPage() {
  const [events, setEvents] = useState<EventItem[]>([]);
  const [selectedEventId, setSelectedEventId] = useState<string>('');
  const [eventsLoading, setEventsLoading] = useState(true);

  const [polledItems, setPolledItems] = useState<GxFeedItem[]>([]);
  const [selectedZones, setSelectedZones] = useState<string[]>([]);
  const [feedName, setFeedName] = useState('Kongosa Box');
  const [muted, setMuted] = useState(false);

  // Fetch events on mount
  useEffect(() => {
    async function fetchEvents() {
      try {
        const data = await getEvents();
        setEvents(data);
        const legacyBall = data.find((e) => e.id === LEGACY_BALL_ID);
        if (legacyBall) {
          setSelectedEventId(legacyBall.id);
        } else if (data.length > 0) {
          setSelectedEventId(data[0].id);
        }
      } catch {
        setEvents([]);
      } finally {
        setEventsLoading(false);
      }
    }
    fetchEvents();
  }, []);

  // Reset polled items when event changes
  useEffect(() => {
    setPolledItems([]);
    setSelectedZones([]);
  }, [selectedEventId]);

  // Fetch GX config on mount
  useEffect(() => {
    getGxConfig()
      .then((config) => {
        setFeedName(config.feedName);
        setMuted(config.muted);
      })
      .catch(() => {
        // Use defaults on error
      });
  }, []);

  const { subscription, isLoading: subLoading, subscribe } = useGxSubscription(selectedEventId);
  const { zones } = useGxZones(selectedEventId);
  const zoneFilter = selectedZones.length > 0 ? selectedZones : undefined;
  const { items: feedItems, isLoading: feedLoading, loadMore, hasMore } = useGxFeed(selectedEventId, zoneFilter);

  const isSubscribed = subscription !== null;

  // Auto-subscribe on first visit
  useEffect(() => {
    if (!subLoading && !isSubscribed && selectedEventId) {
      subscribe().catch(() => {
        // Ignore conflict errors (already subscribed)
      });
    }
  }, [subLoading, isSubscribed, subscribe, selectedEventId]);

  const handleNewItems = useCallback((newItems: GxFeedItem[]) => {
    setPolledItems((prev) => [...newItems, ...prev]);
  }, []);

  const { isPolling, lastPollTime, error: pollingError } = useGxPolling(selectedEventId, isSubscribed, handleNewItems);

  // Combine polled items (newest) with initial feed items
  const allItems = [...polledItems, ...feedItems];

  // Only show active zones in the filter bar
  const activeZones = zones.filter((z) => z.active);

  const handleToggleZone = useCallback((zoneId: string) => {
    setSelectedZones((prev) =>
      prev.includes(zoneId) ? prev.filter((id) => id !== zoneId) : [...prev, zoneId],
    );
  }, []);

  const handleNoteCreated = useCallback((item: GxFeedItem) => {
    setPolledItems((prev) => [item, ...prev]);
  }, []);

  const handleAcknowledge = useCallback(
    async (itemId: string) => {
      try {
        const updated = await acknowledgeFeedItem(selectedEventId, itemId);
        setPolledItems((prev) =>
          prev.map((item) =>
            item.id === itemId
              ? { ...item, acknowledged: updated.acknowledged, acknowledgedAt: updated.acknowledgedAt, acknowledgedBy: updated.acknowledgedBy }
              : item,
          ),
        );
      } catch {
        // Acknowledge error handled silently
      }
    },
    [selectedEventId],
  );

  const selectedEventName = events.find((e) => e.id === selectedEventId)?.name || 'Event';

  if (eventsLoading) {
    return (
      <div style={pageStyles.container}>
        <p style={pageStyles.loading}>Loading...</p>
      </div>
    );
  }

  return (
    <div style={pageStyles.container}>
      {/* Header */}
      <div style={pageStyles.header}>
        <h1 style={pageStyles.title}>{feedName}</h1>
        <p style={pageStyles.subtitle}>Live feed for {selectedEventName}</p>
      </div>

      {/* Event Selector */}
      <div style={pageStyles.eventSelector}>
        <label style={pageStyles.eventLabel}>Event</label>
        <select
          style={pageStyles.eventSelect}
          value={selectedEventId}
          onChange={(e) => setSelectedEventId(e.target.value)}
        >
          {events.map((evt) => (
            <option key={evt.id} value={evt.id}>
              {evt.name}
            </option>
          ))}
        </select>
      </div>

      {/* Muted Banner */}
      {muted && (
        <div style={pageStyles.mutedBanner}>
          <span>🔇</span>
          <span>The feed is currently muted by an organizer</span>
        </div>
      )}

      {/* Polling Status */}
      <PollingStatusBar
        isPolling={isPolling}
        lastPollTime={lastPollTime}
        error={pollingError}
        isSubscribed={isSubscribed}
      />

      {/* Zone Filter Bar */}
      <div style={pageStyles.section}>
        <div style={pageStyles.sectionLabel}>Zone Filter</div>
        <ZoneFilterBar
          zones={activeZones}
          selectedZones={selectedZones}
          onToggleZone={handleToggleZone}
        />
      </div>

      {/* Live Feed */}
      <div style={pageStyles.section}>
        <div style={pageStyles.sectionLabel}>Live Feed</div>
        {feedLoading ? (
          <p style={pageStyles.loading}>Loading feed...</p>
        ) : (
          <LiveFeedView
            items={allItems}
            onAcknowledge={handleAcknowledge}
            onLoadMore={loadMore}
            hasMore={hasMore}
          />
        )}
      </div>

      {/* Post Note */}
      <div style={pageStyles.noteInputWrapper}>
        <div style={pageStyles.sectionLabel}>Post a Note</div>
        <LiveNoteInput
          eventId={selectedEventId}
          zones={zones}
          onNoteCreated={handleNoteCreated}
        />
      </div>
    </div>
  );
}
